import type { QualityTier, TierProfile } from '~/lib/performanceTiers';

import { useCallback, useEffect, useRef, useState } from 'react';
import { computeCapabilityScore, isSoftwareRenderer, readCapabilitySignals } from '~/lib/capabilityScore';
import { nextTierDown, profileFor, scoreToTier, TIER_MONITOR_CONFIG } from '~/lib/performanceTiers';

export interface AdaptiveTierState {
  tier: QualityTier;
  profile: TierProfile | null;
  capabilityScore: number;
  measuredFps: number | null;
  onDecline: () => void;
  setMeasuredFps: (fps: number) => void;
}

interface Assessment {
  tier: QualityTier;
  score: number;
}

/**
 * @returns The current high-resolution timestamp, or 0 when the performance API is unavailable
 */
function now(): number {
  return 'undefined' !== typeof performance ? performance.now() : 0;
}

/**
 * @returns The one-shot capability assessment read from the current client
 */
function assess(): Assessment {
  const signals = readCapabilitySignals();
  const score = computeCapabilityScore(signals);
  const tier = isSoftwareRenderer(signals.rendererString) ? 'off' : scoreToTier(score, signals.isMobile);
  return { tier, score };
}

/**
 * @param tier - Starting tier
 * @param steps - Number of levels to step down
 * @returns The tier `steps` levels below `tier`, clamped at `off`
 */
function stepDown(tier: QualityTier, steps: number): QualityTier {
  let result = tier;
  for (let i = 0; i < steps; i += 1) {
    result = nextTierDown(result);
  }
  return result;
}

/**
 * Resolves the initial quality tier from static capability once the readiness gate has passed,
 * then exposes the active profile and a decline callback that steps the tier down. Tiering is
 * downward-only: a decline steps the tier one level lower, dwell-gated, and stops at `off`.
 *
 * Mount this once (via `TierProvider`) and share the result through `useTier`; running two
 * instances would probe twice and drive two independent canvases.
 *
 * @param options.eligible - Whether the client is ready, not a bot, and has hardware WebGL
 * @param options.override - Optional tier override; wins over the computed tier for renderable tiers
 * @returns The active tier, its profile, the capability score, and the runtime decline/fps callbacks
 */
export function useAdaptiveTier(options: {
  eligible: boolean;
  override?: QualityTier | null;
}): AdaptiveTierState {
  const { eligible, override } = options;

  const assessmentRef = useRef<Assessment | null>(null);
  if (eligible && null === assessmentRef.current) {
    assessmentRef.current = assess();
  }
  const assessment = assessmentRef.current;

  const dwellStartRef = useRef(0);
  const [downgrades, setDowngrades] = useState(0);
  const [measuredFps, setMeasuredFps] = useState<number | null>(null);

  const baseTier = assessment?.tier ?? 'off';
  const tier = stepDown(baseTier, downgrades);
  const samplingDisabled = 'off' === tier;

  useEffect(() => {
    if ('off' !== tier) {
      dwellStartRef.current = now();
    }
  }, [tier]);

  const onDecline = useCallback(() => {
    if (samplingDisabled) {
      return;
    }
    const elapsed = now() - dwellStartRef.current;
    if (elapsed < TIER_MONITOR_CONFIG.minDwellMs) {
      return;
    }
    setDowngrades(count => count + 1);
  }, [samplingDisabled]);

  const resolvedTier = null != override && 'off' !== tier && 'off' !== override ? override : tier;

  return {
    tier: resolvedTier,
    profile: profileFor(resolvedTier),
    capabilityScore: assessment?.score ?? 0,
    measuredFps,
    onDecline,
    setMeasuredFps,
  };
}
